import React, { Component } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from 'styled-components'
import VirtualScroll from "react-dynamic-virtual-scroll";
import shortid from 'shortid'

import BaseArrow from '../../../../lib/components/Arrow'
import Tooltip, { withTooltip } from '../../../../lib/components/Tooltip'
import bindActionCreators from '../../../../lib/utils/bindActionCreators'
import { toggleOrder } from '../../../../lib/utils'
import { momentFormatter, financialFormatter, parseFinancial } from '../../../../lib/utils'

import { PATH, HEADER } from '../../../project-information/constants'

import { getTable, getSort } from '../selectors'
import { setSort } from '../actions'

import withRedirect from '../hocs/withRedirect'
import ScrollWrapper from '../contexts/ScrollWrapper'

/**
 * @typedef {import('../model').PPSReport} PPSReport
 */

const Root = styled.table`
  width: 100%;
  height: 100%;
  display: block;
`

const THead = styled.thead`
  height: 50px;

  display: flex;
  align-items: center;

  background-color: #404040;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08);
`

export const TBody = styled.tbody`
  max-height: calc(100% - 50px);
  padding-bottom: 24px;

  display: block;
  background-color: #fff;

  overflow-x: hidden;
  overflow-y: scroll;

  > * {
    border-bottom: 1px solid #ddd;
    cursor: pointer;
  }

  > :hover {
    background-color: #ededed;
  }
`

export const Tr = styled.tr`
  width: 100%;
  height: 45px;

  display: grid;
  grid-template-columns:
    minmax(150px, 1fr)
    125px
    140px
    80px
    150px
    100px
    minmax(150px, 1fr)
    120px
    130px
    120px
    minmax(150px, 1fr)
    120px;
  grid-template-rows: 100%;
  align-items: center;

  > * {
    border-left: 1px solid #dee2e6;
  }

  > :first-child {
    padding-left: 16px;
    border-left: none;
  }

  > :last-child {
    padding-right: 16px;
  }
`

const Th = styled.th`
  width: 100%;
  height: 100%;
  padding: 0 12px;

  display: flex;
  align-items: center;

  color: #fff;
  font-family: 'NotoSans' !important;
  font-size: 14px;

  cursor: pointer;
`

const Td = styled.td`
  width: 100%;
  height: 100%;
  padding: 0 12px;

  display: flex;
  align-items: center;

  color: #333333;
  font-family: 'NotoSans';
  font-size: 14px;
  line-height: 1.4em;

  ${withTooltip}

  ${Tooltip} {
    top: 80%;
  }

  &:hover ${Tooltip} {
    transition-delay: 1s;
  }
`

const Ellipsis = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Arrow = styled(BaseArrow)`
  pointer-events: none;
`

/**
 * @typedef {object} Props
 * @property {{
 *    date: string,
 *    employee_id: string,
 *    employee_name: string,
 *    lob_name: string,
 *    project_code: string,
 *    location_name: string,
 *    task: string,
 *    approver_name: string,
 *    status: string,
 *    remark: string
 * }[]} data
 * @property {(payload: { to: string }) => void} redirect
 * @property {object} sort
 * @property {typeof setSort} setSort
 */

/** @extends {Component<Props>} */
export class Table extends Component {
  /** @type {Props} */
  static defaultProps = {
    data: [],
    sort: {},
    redirect: () => { },
    setSort: () => { },
  }

  /** @type {(id: string) => () => void} */
  // toInformation = id => () =>
  //   this.props.redirect({
  //     to: `${PATH}/${id}`,
  //     header: HEADER,
  //   })

  /** @type {(key: string) => () => void} */
  handleColumnClick = key => () => {
    const { setSort, sort } = this.props

    if (key === sort.field) {
      setSort('order', toggleOrder(sort.order))
    } else {
      setSort('field', key)
    }
  }

  /**
   * @param {string} field
   */
  renderArrow = field => {
    const { sort } = this.props
    const ORDER_DIRECTION = { asc: 'up', desc: 'down' }
    return sort.field === field ? <Arrow direction={ORDER_DIRECTION[sort.order]} /> : null
  }

  render = () => {
    const { data } = this.props
    return (
      <Root>
        <THead>
          <Tr style={{ height: '100%' }}>
            <Th onClick={this.handleColumnClick('date')}>
              Date
              {this.renderArrow('date')}
            </Th>
            <Th onClick={this.handleColumnClick('employee_id')}>
              Employee ID
              {this.renderArrow('employee_id')}
            </Th>
            <Th onClick={this.handleColumnClick('employee_name')}>
              Employee Name
              {this.renderArrow('employee_name')}
            </Th>
            <Th onClick={this.handleColumnClick('lob_name')}>
              LOB
              {this.renderArrow('lob_name')}
            </Th>
            <Th onClick={this.handleColumnClick('project_code')}>
              Project Code
              {this.renderArrow('project_code')}
            </Th>
            <Th>
              Location
            </Th>
            <Th>
              Task
            </Th>
            <Th>
              Approver Name
            </Th>
            <Th>
              Status
            </Th>
            <Th>
              Reamrk
            </Th>
          </Tr>
        </THead>
        <ScrollWrapper.Consumer>
          {Wrapper => (
            <Wrapper>
              <TBody>
                {data &
                  <VirtualScroll
                    minItemHeight={45}
                    totalLength={(data || []).length}
                    renderItem={(rowIndex) => {
                      const row = (data || [])[rowIndex]

                      return (
                        <Tr key={shortid.generate()}>
                          <Td style={{ fontFamily: 'NotoSans' }}>{row.date}</Td>
                          <Td>{row.employee_id}</Td>
                          <Td>
                            <Ellipsis>{row.employee_name}</Ellipsis>
                            <Tooltip>{row.employee_name}</Tooltip>
                          </Td>
                          <Td>{row.lob_name}</Td>
                          <Td>{row.project_code}</Td>
                          <Td>{row.location_name}</Td>
                          <Td>
                            <Ellipsis>{row.task}</Ellipsis>
                            <Tooltip>{row.task}</Tooltip>
                          </Td>
                          <Td>{row.approver_name}</Td>
                          <Td>{row.status}</Td>
                          <Td>
                            <Ellipsis>{row.remark}</Ellipsis>
                            <Tooltip>{row.remark}</Tooltip>
                          </Td>
                        </Tr>
                      );
                    }}
                  />
                }
                {/* {data &&
                  data.map(row => (
                    <Tr key={shortid.generate()}>
                      <Td style={{ fontFamily: 'NotoSans' }}>{row.date}</Td>
                      <Td>{row.employee_id}</Td>
                      <Td>
                        <Ellipsis>{row.employee_name}</Ellipsis>
                        <Tooltip>{row.employee_name}</Tooltip>
                      </Td>
                      <Td>{row.lob_name}</Td>
                      <Td>{row.project_code}</Td>
                      <Td>{row.location_name}</Td>
                      <Td>
                        <Ellipsis>{row.task}</Ellipsis>
                        <Tooltip>{row.task}</Tooltip>
                      </Td>
                      <Td>{row.approver_name}</Td>
                      <Td>{row.status}</Td>
                      <Td>
                        <Ellipsis>{row.remark}</Ellipsis>
                        <Tooltip>{row.remark}</Tooltip>
                      </Td>
                    </Tr>
                  ))} */}
              </TBody>
            </Wrapper>
          )}
        </ScrollWrapper.Consumer>
      </Root>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  data: getTable,
  sort: getSort,
})

const mapDispatchToProps = bindActionCreators({
  setSort,
})

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withRedirect
)(Table)



// WEBPACK FOOTER //
// ./src/pages/report/summary-timesheet/components/Table.jsx